import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import LearningZoneShell, { useLearningZoneSidebar } from '../../components/student/learningzone/LearningZoneShell';
import { LZ_SCROLL_REGION } from '../../components/student/learningzone/LearningSidebar';
import WallOfFame from '../../components/wtf/WallOfFame';
import DiaryZone from '../../components/student/learningzone/DiaryZone';
import MedicalHistoryView from '../../components/student/learningzone/MedicalHistoryView';
import GComprisZone from '../../components/student/computer-apps/GComprisZone';
import CourseContentView from '../../components/student/learningzone/CourseContentView';

/**
 * Learning Zone - Epic 07 (course browser)
 * Scenes, Action Songs, Stories and the art rows (Computer Apps > Artweaver) show the published
 * course content placed in them (or say nothing has been added yet) - never sample items.
 * The sidebar's "WTF" item swaps the content area to the existing Wall of
 * Fame feature (reused as-is) instead of navigating away from this page.
 * WTF is the landing view (students arrive here straight after login);
 * closing it drops back to English > Scenes.
 * A row picked in the sidebar of the student's Shop pages (same frame, see
 * StudentShopLayout) comes in as the location state `{ view }` and opens here.
 *
 * The top bar, sidebar and scroll layout are the shared LearningZoneShell.
 */
export default function LearningZonePage() {
  const { state } = useLocation();
  const [activeView, setActiveView] = useState(() => state?.view || 'wtf'); // 'wtf' | 'diary' | 'gcompris' | 'medical' | course content views

  // The sidebar rows, including the courses' task types / added subcategories / added categories.
  const { tree: sidebarTree, views: courseViews } = useLearningZoneSidebar();

  return (
    <LearningZoneShell sidebarTree={sidebarTree} activeView={activeView} onSelectView={setActiveView}>
      {activeView === 'wtf' ? (
        <div className={`flex-1 min-w-0 flex flex-col ${LZ_SCROLL_REGION}`}>
          {/* Back to what English opens: its Scenes content row. */}
          <WallOfFame autoHeight onClose={() => setActiveView(sidebarTree.find((group) => group.id === 'english')?.view || 'wtf')} />
        </div>
      ) : activeView === 'diary' ? (
        <div className={`flex-1 min-w-0 ${LZ_SCROLL_REGION}`}>
          <DiaryZone />
        </div>
      ) : activeView === 'gcompris' ? (
        <div className={`flex-1 min-w-0 ${LZ_SCROLL_REGION}`}>
          <GComprisZone />
        </div>
      ) : activeView === 'medical' ? (
        // Container-query shell, keyed so coming back refetches. w-full: below lg
        // the parent is a flex-col with items-start, and a size-contained
        // (container-type) child would otherwise collapse to ~0 width; at lg+
        // flex-1 sets the width so w-full is inert.
        <div key="medical" className={`w-full flex-1 min-w-0 [container-type:inline-size] ${LZ_SCROLL_REGION}`}>
          <MedicalHistoryView />
        </div>
      ) : courseViews[activeView] ? (
        // A row's course content (Scenes, Action Songs, Stories, Artweaver's art lessons, a task type
        // row such as English > Revision, an added subcategory), each in its own screen's look:
        // same shell as the views above (the art drawing board's layout keys off it
        // too), keyed so switching rows starts on the first chapter.
        <div key={activeView} className={`w-full flex-1 min-w-0 [container-type:inline-size] ${LZ_SCROLL_REGION}`}>
          <CourseContentView content={courseViews[activeView]} />
        </div>
      ) : null}
    </LearningZoneShell>
  );
}
